/**
 * #1389 — what the board, the prop rows and the Injuries disclosure say about
 * one game's injury block (hydracore `HYDRA_API.md` § Injuries; G1 copy in
 * `HYDRA_API.md`). Pure: the views read these and nothing else.
 *
 * Invariants:
 * - Every official row counts, G League and Not With Team included. A count is
 *   of report statuses, not of injured players; filtering on `reason` text is a
 *   heuristic the contract leaves to a view, and no view here takes it.
 * - Unavailable (no block), pending (`filed: false`) and filed with nothing to
 *   report are three different states and never share a rendering.
 * - A player's status comes ONLY from an exact match on the wire `playerKey`.
 *   `unpaired` / `ambiguous` rows carry no key and are never matched by name.
 *   A player absent from the report stays UNKNOWN: no "playing" and no "not
 *   listed" is inferred, because nothing on the wire yet proves canonical team
 *   membership and rules out an unlinked row being the same player.
 * - Report age is the browser's wall clock against `reportAt`, the NBA slot's
 *   own start (a calendar time, not a producer receipt time). It is a reading
 *   aid beside the exact ET time in the disclosure, never a staleness verdict:
 *   there is no threshold, and a browser clock behind the slot reads "now".
 */
import type { InjuryBlock, InjuryPlayer, InjuryView } from './injuries';

export type InjuryStatus = InjuryPlayer['status'];

/** Severity order: the order counts, rows and tokens are shown in. */
export const INJURY_STATUS_ORDER: readonly InjuryStatus[] = ['out', 'doubtful', 'questionable', 'probable', 'available'];

export const INJURY_STATUS_LETTER: Record<InjuryStatus, string> = {
  out: 'O', doubtful: 'D', questionable: 'Q', probable: 'P', available: 'A',
};

const STATUS_WORD: Record<InjuryStatus, string> = {
  out: 'out', doubtful: 'doubtful', questionable: 'questionable', probable: 'probable', available: 'available',
};

export type InjuryCounts = Record<InjuryStatus, number>;

export type TeamInjuryState =
  | { kind: 'unavailable' }
  | { kind: 'pending' }
  | { kind: 'clean' }
  | { kind: 'listed'; counts: InjuryCounts; total: number };

/** One team's state in a block. A team the block does not carry (or a code the
 *  row spells differently) is unavailable, never guessed. */
export function teamInjuryState(block: InjuryBlock | null | undefined, team: string | null | undefined): TeamInjuryState {
  const entry = block && team ? block.teams[team] : undefined;
  if (!entry) return { kind: 'unavailable' };
  if (!entry.filed) return { kind: 'pending' };
  if (entry.players.length === 0) return { kind: 'clean' };
  const counts: InjuryCounts = { out: 0, doubtful: 0, questionable: 0, probable: 0, available: 0 };
  for (const p of entry.players) counts[p.status]++;
  return { kind: 'listed', counts, total: entry.players.length };
}

/** Non-zero statuses in severity order: `O2 Q1`. All of them — the total of
 *  the tokens is always every official row, and nothing hides behind a `+n`. */
export function injuryCountTokens(counts: InjuryCounts): { status: InjuryStatus; letter: string; count: number }[] {
  return INJURY_STATUS_ORDER.filter((s) => counts[s] > 0)
    .map((status) => ({ status, letter: INJURY_STATUS_LETTER[status], count: counts[status] }));
}

/** "1 out, 1 doubtful" / "pending" / "none listed" / "unavailable". */
export function teamInjuryWords(state: TeamInjuryState): string {
  switch (state.kind) {
    case 'unavailable': return 'report unavailable';
    case 'pending': return 'report not yet submitted';
    case 'clean': return 'filed, no players listed';
    case 'listed': return injuryCountTokens(state.counts).map((t) => `${t.count} ${STATUS_WORD[t.status]}`).join(', ');
  }
}

/** The compact text a board entry shows for a state (without the age). */
export function teamInjuryShort(state: TeamInjuryState): string {
  switch (state.kind) {
    case 'unavailable': return '—';
    case 'pending': return 'pending';
    case 'clean': return 'none';
    case 'listed': return injuryCountTokens(state.counts).map((t) => `${t.letter}${t.count}`).join(' ');
  }
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Whole minutes since the report slot, clamped at 0 (see the header). */
function ageMs(reportAt: string, nowMs: number): number | null {
  const at = Date.parse(reportAt);
  if (!Number.isFinite(at) || !Number.isFinite(nowMs)) return null;
  return Math.max(0, nowMs - at);
}

/** "now" / "32m" / "5h" / "2d" — fixed small width for the board. */
export function reportAgeShort(reportAt: string, nowMs: number): string {
  const age = ageMs(reportAt, nowMs);
  if (age == null) return '';
  if (age < MINUTE) return 'now';
  if (age < HOUR) return `${Math.floor(age / MINUTE)}m`;
  if (age < DAY) return `${Math.floor(age / HOUR)}h`;
  return `${Math.floor(age / DAY)}d`;
}

/** "under a minute ago" / "32 minutes ago" / "5 hours ago" / "2 days ago". */
export function reportAgeWords(reportAt: string, nowMs: number): string {
  const age = ageMs(reportAt, nowMs);
  if (age == null) return '';
  const unit = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'} ago`;
  if (age < MINUTE) return 'under a minute ago';
  if (age < HOUR) return unit(Math.floor(age / MINUTE), 'minute');
  if (age < DAY) return unit(Math.floor(age / HOUR), 'hour');
  return unit(Math.floor(age / DAY), 'day');
}

const ET_CLOCK = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
const LOCAL_CLOCK = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const ET_DAY = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });

/** "4:15 PM ET" — the NBA report's own clock (its PDF names the ET slot). */
export function reportClockEt(at: string): string {
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? `${ET_CLOCK.format(ms)} ET` : '';
}

/** "Oct 30, 4:15 PM ET" — `statusSince` can be a previous day's report. */
export function reportStampEt(at: string): string {
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? `${ET_DAY.format(ms)}, ${ET_CLOCK.format(ms)} ET` : '';
}

/** "1:15 PM" in the browser's own zone, beside the ET clock. */
export function reportClockLocal(at: string): string {
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? LOCAL_CLOCK.format(ms) : '';
}

/** The source label: the NBA report by its name, any other source verbatim. */
export function injurySourceLabel(source: string): string {
  return source === 'nba-official-report' ? 'NBA official report' : source;
}

/** Rows in severity order, then by name: the order every list shows. */
export function sortInjuryRows(players: readonly InjuryPlayer[]): InjuryPlayer[] {
  const rank = (s: InjuryStatus) => INJURY_STATUS_ORDER.indexOf(s);
  return [...players].sort((a, b) => rank(a.status) - rank(b.status) || a.player.localeCompare(b.player));
}

/** Last-known data under a warning: an invalid update kept the previous block,
 *  or the connection is not live. Report age and this stay separate signals. */
export function injuryEvidenceWarning(row: InjuryView | undefined, status: string, nowMs: number): string | null {
  if (row?.invalid) return 'Invalid update · last known';
  if (status !== 'live') return 'Disconnected · last known';
  if (row && nowMs - row.receivedAt > 15_000) return 'Refresh overdue';
  return null;
}

/**
 * A prop player's report row: an exact match on the wire `playerKey` (the
 * prop ladder's `opPlayerKey` — never a ticker-derived or Polymarket key), or
 * null. Null is unknown, not "playing" (see the header); the prop row shows a
 * status only for an exact match, and the team's pending/filed state lives on
 * the board entry and in the disclosure.
 */
export function propInjuryRow(
  block: InjuryBlock | null | undefined,
  wirePlayerKey: string | null | undefined,
): InjuryPlayer | null {
  if (!block || !wirePlayerKey) return null;
  for (const team of Object.values(block.teams)) {
    const hit = team.players.find((p) => p.pairing === 'paired' && p.playerKey === wirePlayerKey);
    if (hit) return hit;
  }
  return null;
}
